/* eslint-disable @typescript-eslint/no-explicit-any */
import { HStack, TabPanel, TabPanels, VStack } from '@chakra-ui/react';
import { AiOutlineRead, AiOutlineSearch } from 'react-icons/ai';

import { SlEarphones, SlHeart } from 'react-icons/sl';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CategoryTag } from '../../../../../packages/ui/components/atoms/CategoryTag';
import { Input } from '../../../../../packages/ui/components/atoms/Input';
import { useTheme } from '../../../../../packages/ui/contexts/ThemeProvider';
import { TopBookstoreMenu } from '../../components/organisms/TopBookstoreMenu';
import { MainTemplate } from '../../templates/MainTemplate';
import { MyBooksTemplate } from './components/MyBookTemplate';

import {
  useBookCategories,
  useBooks,
  useCombinedReceivedMagazines,
  useGetCombinedBooksMutation,
  useGetFavoritedBooksMutation,
  useMothlyBestSellers,
  useMyReadingBooks,
} from '@partiu/api/bookstore/hooks';
import { BookGrid } from '../../components/organisms/BookGrid';
import { CurrentBook } from '../../components/molecules/CurrentBook';
import { HorizontalBooks } from '../../components/molecules/HorizontalBooks';
import { FaNewspaper } from 'react-icons/fa';
import { InfoBox } from '../../components/atoms/InfoBox';
import { OnboardingBalloon, useBookstoreOnboarding } from '../../components/organisms/BookstoreOnboarding';
import { RiBookLine, RiBookOpenLine } from 'react-icons/ri';
import { BookLoadingOverlay } from '../../components/organisms/BookLoadingOverlay';

interface Category {
  id: number;
  name: string;
}

function useCategoryTags(
  categories: { results: Category[] } | undefined,
  tagSet: Set<number>,
  setTagSet: React.Dispatch<React.SetStateAction<Set<number>>>
) {
  return useMemo(
    () =>
      categories?.results
        .map((c) => ({ ...c, active: tagSet.has(c.id) ? 1 : 0 }))
        .sort((a, b) => b.active - a.active)
        .map((c) => (
          <CategoryTag
            key={c.id}
            active={tagSet.has(c.id)}
            onClick={() => {
              setTagSet((prev) => {
                const newSet = new Set(prev);
                if (newSet.has(c.id)) {
                  newSet.delete(c.id);
                } else {
                  newSet.add(c.id);
                }
                return newSet;
              });
            }}
          >
            {c.name.toLocaleLowerCase()}
          </CategoryTag>
        )),
    [tagSet, categories?.results, setTagSet]
  );
}

export function MyBooksPage() {
  const { bookstoreTypes, product, digitalMagazinesModule, showBestSellers, id } = useTheme();
  const { data: categories } = useBookCategories();
  const [tagSet, setTagSet] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState<string>('');
  const [params] = useSearchParams();

  const isAudiobook = product
    ? product?.metadata?.hasaudiobook?.value === 'true'
    : bookstoreTypes.includes('audiobook');
  const isMagazine = product ? product?.metadata?.hasmagazine?.value === 'true' : bookstoreTypes.includes('magazine');
  const hasDigitalMagazines = digitalMagazinesModule;
  const isBook = isMagazine ? false : product ? product.defaultProduct.isBookstore : bookstoreTypes.includes('book');
  const showBestSellersProduct = product ? product?.metadata?.isbestsellers?.value === 'true' : showBestSellers;
  const shouldMergeBooksAndAudiobooks = isBook && isAudiobook;
  const shouldMergeCatalogBooksAndAudiobooks = isBook && isAudiobook;

  useEffect(() => {
    const categoryParam = params.get('categorias');
    if (!categoryParam) return;
    setTimeout(() => {
      setTagSet(new Set(categoryParam.split(',').map((cid) => parseInt(cid))));
    }, 800);
  }, [params]);

  const tags = useCategoryTags(categories, tagSet, setTagSet);

  const tabs = [
    (isBook || isAudiobook) && {
      name: 'Minhas Obras',
      icon: shouldMergeBooksAndAudiobooks ? <RiBookLine /> : isBook ? <RiBookLine /> : <SlEarphones />,
    },
    isMagazine && { name: 'Minhas Revistas', icon: <FaNewspaper /> },
    { name: 'Acervo', icon: <RiBookOpenLine /> },
    { name: 'Lendo', icon: <AiOutlineRead /> },
    { name: 'Favoritos', icon: <SlHeart /> },
  ].filter(Boolean) as { name: string; icon: JSX.Element }[];

  const acervoTabIndex = tabs.findIndex((t) => t.name === 'Acervo');
  const {
    step: onboardingStep,
    controlled,
    tabIndex,
    handleTabChange,
    confirmMeus,
    confirmAcervo,
  } = useBookstoreOnboarding(acervoTabIndex >= 0 ? acervoTabIndex : 1, isMagazine ? 'banca' : 'biblioteca');

  return (
    <MainTemplate noShadow>
      <BookLoadingOverlay />
      <TopBookstoreMenu tabs={tabs} {...(controlled ? { tabIndex, onTabChange: handleTabChange } : {})}>
        <TabPanels>
          {/* Minhas Obras */}
          {(isBook || isAudiobook) && (
            <TabPanel padding="0px">
              <VStack mt="20px" width="100%" align="flex-start" spacing={5}>
                <CurrentBook />
                <InfoBox
                  title="Minhas Obras"
                  description="Estes e-books e audiobooks são seus para sempre. Com ISBN incluso, você pode baixá-los quando quiser realizando o download."
                  borderColor={shouldMergeBooksAndAudiobooks ? '#14B8A6' : isBook ? '#3B82F6' : '#F59E0B'}
                  bg={shouldMergeBooksAndAudiobooks ? '#F0FDFA' : isBook ? '#EFF6FF' : '#FFFBEB'}
                />
                {onboardingStep === 'meus' && (
                  <OnboardingBalloon
                    text="Essas obras são suas para sempre! Você pode baixar quando quiser e ler em outras plataformas se assim desejar."
                    onConfirm={confirmMeus}
                  />
                )}
                {/*        <HorizontalBooks
                  name={'Ebooks disponibilizados neste mês'}
                  receivedBook
                  params={{ type: 'book', product_id: product?.id }}
                  hookApi={useFeaturedBooks}
                  description={
                    'Estes títulos foram disponibilizados para você e podem ser baixados para uso permanente.'
                  }
                  borderColor="#3B82F6"
                  bg="#EFF6FF"
                />
 */}
                <MyBooksTemplate
                  emptyLabel="obras"
                  filter={{
                    ...(shouldMergeBooksAndAudiobooks ? {} : { book__type: isBook ? 'book' : 'audiobook' }),
                    product_id: product?.id,
                  }}
                />
              </VStack>
            </TabPanel>
          )}

          {/* Minhas Revistas */}
          {isMagazine && (
            <TabPanel padding="0px">
              <VStack mt="20px" width="100%" align="flex-start" spacing={5}>
                <InfoBox
                  title="Minhas Revistas"
                  description="Estas revistas são suas para sempre. Com ISSN incluso, você pode baixá-las quando quiser realizando o download."
                  borderColor="#8B5CF6"
                  bg="#F5F3FF"
                />
                {onboardingStep === 'meus' && (
                  <OnboardingBalloon
                    text="Estas revistas são suas para sempre! Você pode baixá-las quando quiser e ler em outras plataformas se assim desejar."
                    onConfirm={confirmMeus}
                  />
                )}

                {/* <HorizontalBooks
                  name={'Revista do mês'}
                  receivedBook
                  params={{ type: 'magazine', product_id: product?.id }}
                  hookApi={useFeaturedBooks}
                  description={
                    'A edição do mês é carregada automaticamente e integra o histórico de revistas recebidas.'
                  }
                  borderColor="#8B5CF6"
                  bg="#F5F3FF"
                /> */}

                <MyBooksTemplate
                  emptyLabel="revistas"
                  queryHook={hasDigitalMagazines ? useCombinedReceivedMagazines : undefined}
                  filter={{
                    book__type: 'magazine',
                    product_id: product?.id,
                    ...(hasDigitalMagazines && id ? { agreement: id.toString() } : {}),
                  }}
                />
              </VStack>
            </TabPanel>
          )}

          {/* Revistas Personalizadas */}
          {/*       {isMagazine && hasDigitalMagazines && (
            <TabPanel padding="0px">
              <VStack mt="20px" width="100%" spacing={5} align="flex-start">
                <InfoBox
                  title="Revistas Personalizadas"
                  description="Confira todas as revistas personalizadas disponíveis para você, organizadas por mês de referência."
                  borderColor="#8B5CF6"
                  bg="#F5F3FF"
                />
                <MagazineGrid agreementId={id ?? undefined} />
              </VStack>
            </TabPanel>
          )} */}

          {/* Catálogo */}
          <TabPanel padding="0px">
            <VStack mt="20px" width="100%" spacing={5} align="flex-start">
              <InfoBox
                title={isMagazine ? 'Revistas Gratuitas' : 'Ebooks Gratuitos'}
                description={
                  isMagazine
                    ? 'O Acervo abaixo é disponibilizado gratuitamente para leitura online e não integra a aquisição das revistas editoriais.'
                    : 'O Acervo abaixo é disponibilizada gratuitamente para leitura online e não integra a aquisição das obras editoriais.'
                }
                borderColor="#10B981"
                bg="#ECFDF5"
              />
              {onboardingStep === 'acervo' && (
                <OnboardingBalloon
                  text="Este é o seu acervo de títulos gratuitos para leitura diretamente na plataforma"
                  onConfirm={confirmAcervo}
                />
              )}
              {/*               {isMagazine && hasDigitalMagazines && <HorizontalMagazines agreementId={id ?? undefined} limit={5} />}
               */}{' '}
              {isBook && (
                <HorizontalBooks
                  hookApi={useMothlyBestSellers}
                  name="Destaques do mês"
                  bestSellerName="Destaques"
                  showEditorialCode={false}
                  description="Confira as obras que estão em alta e conquistando leitores."
                  params={{ product_id: product?.id, categories: '162417' }}
                  borderColor="#D97706"
                  bg="#FEF3C7"
                />
              )}
              {/*             {showBestSellersProduct && isBook && product?.id && (
                <HorizontalBooks
                  hookApi={useMothlyBestSellers}
                  name="Best Sellers"
                  hideIfEmpty
                  description="As obras mais vendidas do momento."
                  params={{ product_id: product?.id, categories: '143842' }}
                  borderColor="#EF4444"
                  bg="#FEF2F2"
                />
              )} */}
              {showBestSellersProduct && isBook && product?.id && (
                <HorizontalBooks
                  hookApi={useMothlyBestSellers}
                  name="Lançamentos"
                  hideIfEmpty
                  showEditorialCode={false}
                  description="As novidades que acabaram de chegar."
                  params={{ product_id: product?.id, categories: '143843' }}
                  borderColor="#10B981"
                  bg="#ECFDF5"
                />
              )}
              {showBestSellersProduct && isBook && product?.id && (
                <HorizontalBooks
                  hookApi={useMothlyBestSellers}
                  name="Volta às Aulas"
                  hideIfEmpty
                  showEditorialCode={false}
                  description="Seleção especial para o início do ano letivo."
                  params={{ product_id: product?.id, categories: '143844' }}
                  borderColor="#06B6D4"
                  bg="#ECFEFF"
                />
              )}
              {showBestSellersProduct && isBook && product?.id && (
                <HorizontalBooks
                  hookApi={useMothlyBestSellers}
                  name="Negócios"
                  hideIfEmpty
                  showEditorialCode={false}
                  description="Os melhores títulos sobre negócios e empreendedorismo."
                  params={{ product_id: product?.id, categories: '143913' }}
                  borderColor="#6366F1"
                  bg="#EEF2FF"
                />
              )}
              {showBestSellersProduct && isBook && product?.id && (
                <HorizontalBooks
                  name="Explore os nossos Best Sellers"
                  params={{ type: 'book', random: 'true', is_best_seller: true, product_id: product?.id }}
                  hookApi={useBooks}
                  showEditorialCode={false}
                  description="Confira nossa seleção de obras que estão em alta. Descubra novos títulos para sua coleção!"
                  borderColor="#EC4899"
                  bg="#FDF2F8"
                />
              )}
              <HStack
                paddingX="5%"
                w="100%"
                overflowX="auto"
                css={{
                  '&::-webkit-scrollbar': { display: 'none' },
                  '-ms-overflow-style': 'none',
                  'scrollbar-width': 'none',
                  '@media (min-width: 768px)': {
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '10px',
                    overflowX: 'unset',
                  },
                }}
              >
                {tags}
              </HStack>
              <VStack paddingX="5%" spacing={2} paddingBottom="80px" width="100%" margin="auto">
                <Input value={search} onValueChange={setSearch} label="Busca" rightElement={<AiOutlineSearch />} />
                <BookGrid
                  showEditorialCode={false}
                  filters={{
                    search,
                    categories: Array.from(tagSet).join(','),
                    ...(isMagazine ? { type: 'magazine' } : { type: isBook ? 'book' : 'audiobook' }),
                    random: 'true',
                    limit: '50',
                    product_id: product?.id,
                  }}
                />
              </VStack>
            </VStack>
          </TabPanel>

          {/* Lendo */}
          <TabPanel>
            <HStack paddingX="5%" w="100%" overflowX="auto">
              <BookGrid
                mutationHook={useMyReadingBooks}
                emptyTitle="Você ainda não está lendo nenhuma obra"
                emptyDescription="As obras que você começar a ler aparecerão aqui."
              />
            </HStack>
          </TabPanel>

          {/* Favoritos */}
          <TabPanel>
            <HStack paddingX="5%" w="100%" overflowX="auto">
              <BookGrid
                mutationHook={useGetFavoritedBooksMutation}
                emptyTitle="Você ainda não favoritou nenhuma obra"
                emptyDescription="Favorite obras para encontrá-las facilmente aqui."
              />
            </HStack>
          </TabPanel>
        </TabPanels>
      </TopBookstoreMenu>
    </MainTemplate>
  );
}
